import React, { FC, useEffect } from "react";
import {
  ActionButtonsModel,
  ActionTypeEnum,
  AcTransactionTypeEnum,
  PageEnum,
  PrintTypeEnum,
  RequestActionModel,
  RowStateEnum,
  TransactionDueDetailResponseModel,
} from "../../models";
import {
  AcTransactionForm,
  AcTransactionList,
  ConfirmModelDialogBox,
  ErrorValidationBox,
  GenericModelPopupPdfReportViewer,
  LoadingBox,
  ModelDialogBox,
  PdfViewer,
  ToastBox,
} from "..";
import { getLabelName, getUserId } from "../../utils";
import { Accordion } from "react-bootstrap";
import { deleteAcTransaction } from "../../serviceBroker/acTransactionsApiServiceBroker";
import { useAcTransaction } from "../../hooks";
import { useLocation, useSearchParams } from "react-router-dom";
import { isMoneyInTransaction } from "./businessLogic/acTransactionBl";

export const AcTransactionComponent: FC<{
  acTransactionType: AcTransactionTypeEnum;
  formId: number;
}> = ({ acTransactionType, formId }) => {
  //#region state
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {
    loading,
    setLoading,
    isRefresh,
    setIsRefresh,
    toastModel,
    setToastModel,
    showDeleteModel,
    setShowDeleteModel,
    showPrintModel,
    setShowPrintModel,
    acTransaction,
    setAcTransaction,
    validationErrors,
    accountLookups,
    inOutAccountForPaymentLookups,
    currencyLookUps,
    coastCenterLookUps,
    userLookUps,
    showPrintoutPdfModel,
    setShowPrintoutPdfModel,
    printTransactionId,
    fetchLookupCompleted,
    employeeLookUps,
    handleAction,
  } = useAcTransaction(acTransactionType);
  //#endregion
  //#region functions
  const extractTransactionParams = (searchParams: URLSearchParams) => {
    if (!location.state) {
      return null;
    }
    const paymentTransParamValue = searchParams.get("K8VaI");
    if (!paymentTransParamValue) {
      return null;
    }
    try {
      const objectFromState =
        location.state as TransactionDueDetailResponseModel;
      if (objectFromState.TransactionId != null) {
        return {
          formId: PageEnum.CustomersPayments,
          acTransactionType: AcTransactionTypeEnum.CustomerPayment,
          state: objectFromState,
        };
      }
    } catch (error) {
      return null;
    }
    const objectFromState: TransactionDueDetailResponseModel =
      location.state as TransactionDueDetailResponseModel;
    if (paymentTransParamValue && objectFromState) {
      return {
        formId: PageEnum.CustomersPayments,
        acTransactionType: AcTransactionTypeEnum.CustomerPayment,
        state: objectFromState,
      };
    }
    return null;
  };
  const setTransactionFromParams = (
    objectFromState: TransactionDueDetailResponseModel,
    acTransactionType: AcTransactionTypeEnum
  ) => {
    if (!objectFromState) return;
    const isMoneyIn = isMoneyInTransaction(acTransactionType);
    setAcTransaction((prevState) => ({
      ...prevState, // Preserve the previous state properties
      AcTransactionType: acTransactionType,
      CreatedBy: getUserId(),
      CreationDate: new Date(),
      Date: new Date(),
      Note: objectFromState.Remarks,
      FromAccount_ID: isMoneyIn
        ? objectFromState.AccountId
        : prevState.FromAccount_ID,
      ToAccount_ID: !isMoneyIn
        ? objectFromState.AccountId
        : prevState.ToAccount_ID,
      isNewRequest: true,
      rowState: RowStateEnum.Add,
      //TransactionId: objectFromState.TransactionId,
      CostCenter_ID: objectFromState.CostCenterId,
      Value: objectFromState.DueAmount,
      TransactionDueDate_ID: objectFromState.TransactionDueDateId,
    }));
  };
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      const transactionParams = extractTransactionParams(searchParams);
      if (transactionParams != null) {
        setTransactionFromParams(
          transactionParams.state,
          transactionParams.acTransactionType
        );
      }
    };
    fillData().then(() => {});
  }, [searchParams]);
  //#endregion
  //#region variables
  const deleteActions: ActionButtonsModel[] = [
    {
      text: getLabelName("yes"),
      variant: "danger",
      onClick: async () => {
        setLoading(true);
        const result = await deleteAcTransaction(
          acTransaction?.ID || 0,
          getUserId()
        );
        if (result.Errors != null && result.Errors.length > 0) {
          await handleAction({
            action: ActionTypeEnum.DeleteFailed,
            request: result.Errors,
          });
        } else {
          await handleAction({ action: ActionTypeEnum.DeleteSuccess });
        }
        setLoading(false);
      },
    },
    {
      text: getLabelName("No"),
      onClick: () => {
        setShowDeleteModel(false);
      },
    },
  ];
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      {<ErrorValidationBox errors={validationErrors} />}
      {showPrintoutPdfModel && (
        <GenericModelPopupPdfReportViewer
          keys={[{ key: "transactionId", value: printTransactionId }]}
          type={PrintTypeEnum.AcTransaction}
          onCloseEvent={() => {
            setShowPrintoutPdfModel(false);
          }}
        />
      )}
      {toastModel.show && (
        <ToastBox
          isShown={toastModel.show}
          header={toastModel.header}
          body={toastModel.body}
          variant={toastModel.variant}
          delayDuration={toastModel.delayDuration}
          onCloseEvent={() => {
            setToastModel({ ...toastModel, show: false });
          }}
        />
      )}
      {/* ac transaction form*/}
      {fetchLookupCompleted && (
        <AcTransactionForm
          acTransactionType={acTransactionType}
          request={acTransaction}
          emloyeeList={employeeLookUps}
          setLoading={setLoading}
          accountList={accountLookups}
          inOutAccountList={inOutAccountForPaymentLookups}
          currencyList={currencyLookUps}
          coastCenterList={coastCenterLookUps}
          onActionEvent={async (o: RequestActionModel) => {
            await handleAction(o);
          }}
          formId={formId}
        />
      )}
      {/* ac transaction list*/}
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{getLabelName("Previous Data")}</Accordion.Header>
          <Accordion.Body>
            <AcTransactionList
              acTransactionType={acTransactionType}
              accountLookups={accountLookups}
              userLookUps={userLookUps}
              setIsRefresh={setIsRefresh}
              isRefresh={isRefresh}
              onActionEvent={async (o: RequestActionModel) => {
                await handleAction(o);
              }}
              formId={formId}
            />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <ConfirmModelDialogBox
        isModelVisible={showDeleteModel}
        onCloseEvent={() => {
          setShowDeleteModel(false);
        }}
        actions={deleteActions}
      >
        <>{getLabelName("Are You Sure You Want Delete ")}</>
      </ConfirmModelDialogBox>
      <ModelDialogBox
        isModelVisible={showPrintModel.show}
        isCloseButtonVisible={true}
        onCloseEvent={() => {
          setShowPrintModel({ show: false, content: "" });
        }}
      >
        <PdfViewer content={showPrintModel.content} />
      </ModelDialogBox>
    </>
  );
  //#endregion
};
